import { Build } from "~/components/build/Show/lib/types";
import UserAvatar, { User } from "~/components/shared/UserAvatar";
import { Outcome, State } from "~/lib/pipeline";
import { Node, NodeProps } from "@xyflow/react";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import Button from "~/components/shared/Button";
import BuildStatusDescription from "~/components/shared/BuildStatusDescription";
import BuildTriggerLabel from "~/components/shared/BuildTriggerLabel";
import BuildMessage from "~/components/build/Message";
import { shortCommit } from "~/lib/commits";
import BaseNode from "~/components/Playground/Node/BaseNode";
import { useCallback } from "react";
import Icon from "~/components/shared/Icon";
import { twMerge } from "tailwind-merge";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";

type BuildNodeType = Node<Build & Record<string, unknown>, "build">;

export const BuildNode = ({ data: build, ...props }: NodeProps<BuildNodeType>) => {
  const { missingDependencies } = useStepDependencies(props.id);

  const user: User = {
    name: build.authorName,
    avatar: { url: build.authorAvatar || "" },
  };

  const buildMessageSecondLine = build.message?.split("\n")[2] || "";

  const handleCommitClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <HoverCard.Root>
      <HoverCard.Portal>
        <HoverCard.Content
          {...props}
          noBorder={true}
          state={State.Ready}
          outcome={Outcome.Neutral}
          missingDependencies={missingDependencies}
        >
          <div className="flex w-auto flex-col items-start divide-y">
            <div className="flex flex-row items-center gap-1">
              <div className="self-start">
                <CommitProviderIcon build={build} />
              </div>
              <div className="flex w-full flex-col">
                <BuildMessage message={build.message} url={build.commitUrl || ""} />
                {buildMessageSecondLine && buildMessageSecondLine.length > 0 && (
                  <span className="dark-gray mx-auto line-clamp-3 w-full max-w-xs overflow-hidden text-ellipsis break-normal break-words">
                    {buildMessageSecondLine}
                  </span>
                )}
              </div>
            </div>
            <div className="mt-4 flex w-full flex-1 flex-col gap-2 pt-4">
              <div className="flex w-full flex-row items-center gap-2">
                <UserAvatar user={user} className="h-10 w-10" />

                <div className="flex flex-1 flex-col flex-nowrap">
                  <div className="flex flex-row flex-nowrap items-center justify-center gap-1">
                    <span className="font-medium">{user.name}</span>
                    <span className="dark-gray build-secondary-time ml-auto flex items-center gap-2">
                      <BuildTriggerLabel source={build.source} />
                    </span>
                  </div>
                  <span className="dark-gray flex items-center gap-2">
                    <BuildStatusDescription build={build} />
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex flex-row flex-nowrap gap-1">
                  <span className="font-medium">Build:</span>
                  <a
                    className="color-inherit hover-color-inherit"
                    href={build.path}
                    target="_blank"
                    rel="noreferrer"
                  >
                    #{build.number}
                  </a>
                </div>
                <div className="flex flex-row flex-nowrap gap-1">
                  <span className="font-medium">Branch:</span>
                  <a
                    className="color-inherit hover-color-inherit"
                    href={build.branchPath}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {build.branchName}
                  </a>
                </div>
                <div className="flex flex-row flex-nowrap gap-1">
                  <span className="font-medium">Commit:</span>
                  <a
                    href={build.commitUrl || ""}
                    className="color-inherit hover-color-inherit"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {shortCommit(build.commitId, build.commitShortLength)}
                  </a>
                </div>
              </div>
              <div className="flex flex-row justify-between pt-4">
                <Button
                  href={build.commitUrl || ""}
                  theme="small"
                  target="_blank"
                  onClick={handleCommitClick}
                >
                  View Commit
                </Button>
              </div>
            </div>
          </div>
        </HoverCard.Content>
      </HoverCard.Portal>
      <HoverCard.Trigger asChild={true}>
        <div>
          <BaseNode
            {...props}
            hideStateIcon={true}
            data={{ state: State.Finished, outcome: Outcome.Passed }}
            className="rounded-full p-1"
            icon={<UserAvatar user={user} className="h-10 w-10" />}
          />
        </div>
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
};

const CommitProviderIcon = ({ build }) => {
  const icon = build.project.provider.frontendIcon;
  if (build.commitId) {
    return (
      <Icon
        icon={`custom/solid/provider-${icon}`}
        className={twMerge("mr-1 h-9 w-9 text-current", icon === "bitbucket" && "text-[#205081]")}
      />
    );
  }

  return null;
};
